import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import type { UrlObject } from 'url';

import { Button } from '@/components/Button';
import type { SectionHeaderProps } from '@/components/SectionHeader';
import { ServerErrors as BaseServerErrors } from '@/components/ServerErrors';
import { Stepper } from '@/components/Stepper';
import { useRouter } from '@/hooks/useRouter';
import { PAYMENT_QUERY_PARAMS } from '@/lib/payment/helpers';
import type { WithChildren } from '@/lib/types';
import { commonMessages } from '@/messages/common';

import * as S from './Checkout.styles';
import * as M from './messages';

type HeaderProps = Pick<SectionHeaderProps, 'colorized' | 'text'>;

const Header = ({ colorized = false, ...props }: HeaderProps) => (
  <S.Header
    colorized={colorized}
    sizeDesktop={50}
    sizeMobile={40}
    textAlign='center'
    {...props}
    data-test-id='CheckoutHeader'
  />
);

const Checkout = ({ children }: WithChildren) => (
  <S.Container data-test-id='CheckoutContainer'>{children}</S.Container>
);

interface SectionProps extends WithChildren {
  paddingInlineSpacing?: number;
  withBackground?: boolean;
}

const Section = ({
  children,
  paddingInlineSpacing,
  withBackground = true,
}: SectionProps) => (
  <S.Section
    $paddingInlineSpacing={paddingInlineSpacing}
    data-test-id='CheckoutSection'
    withBackground={withBackground}
  >
    {children}
  </S.Section>
);

const ServerErrors = BaseServerErrors;

interface ContentProps extends WithChildren {
  isFullWidth?: boolean;
  isProcessing?: boolean;
  mainMaxWidth?: number;
  sidebarMaxWidth?: number;
}

const Content = ({
  children,
  isFullWidth,
  isProcessing,
  mainMaxWidth,
  sidebarMaxWidth,
}: ContentProps) => (
  <S.Grid
    $isFullWidth={isFullWidth}
    $isProcessing={!!isProcessing}
    $mainMaxWidth={mainMaxWidth}
    $sidebarMaxWidth={sidebarMaxWidth}
    className='content'
    data-test-id='CheckoutContent'
  >
    {isProcessing && <S.Loader size='lg' thickness={4} />}
    {children}
  </S.Grid>
);

type Step = 'delivery' | 'review' | 'payment';

interface CheckoutStepperProps {
  activeStep: Step;
  disabledSteps?: Step[];
  displaySteps?: boolean;
  onEdit?: (step: Step) => () => void;
  paths?: Partial<Record<Step, UrlObject>>;
  subTitle?: string;
}

const STEP_INDEX: Step[] = ['delivery', 'review', 'payment'];

const CheckoutStepper = ({
  activeStep,
  disabledSteps,
  displaySteps = true,
  onEdit,
  paths,
  subTitle,
}: CheckoutStepperProps) => {
  const { paths: routerPaths } = useRouter();

  const stepperPaths: Record<Step, UrlObject> = {
    delivery: paths?.delivery || routerPaths.store.checkout.delivery.url(),
    review: paths?.review || routerPaths.store.checkout.review.url(),
    payment: paths?.payment || routerPaths.store.checkout.payment.url(),
  };
  const stepIndex = STEP_INDEX.indexOf(activeStep);
  const stepHeader = [M.Delivery, M.Review, M.Payment][stepIndex];
  const stepPaths: { name: Step; path: UrlObject }[] = [
    { path: stepperPaths.delivery, name: 'delivery' },
    { path: stepperPaths.review, name: 'review' },
    { path: stepperPaths.payment, name: 'payment' },
  ];

  const handleEdit = (name: Step) => {
    if (onEdit && !disabledSteps?.includes(name) && activeStep !== name) {
      onEdit(name)();
    }
  };

  return (
    <S.StepperWrapper data-test-id='CheckoutStepperWrapper'>
      {displaySteps && (
        <Stepper activeStep={stepIndex}>
          {stepPaths.map(({ name, path }, i) => (
            <Stepper.Step
              key={name}
              data-test-id={`CheckoutStepperStep-${i}`}
              href={i === stepIndex || stepIndex < i ? undefined : path}
              isDisabled={disabledSteps?.includes(name)}
              onEdit={() => handleEdit(name)}
            />
          ))}
        </Stepper>
      )}
      <S.Subheader>{stepHeader}</S.Subheader>
      {subTitle && (
        <S.SubTitle data-test-id='CheckoutStepperSubTitle'>
          {subTitle}
        </S.SubTitle>
      )}
    </S.StepperWrapper>
  );
};

type LeaveHandler = (step: Step, section?: string) => () => void;

interface LeaveConfirmationModalProps {
  children: (handleStepLeave: LeaveHandler) => JSX.Element;
  paths?: Partial<Record<Step, UrlObject>>;
}

const LeaveConfirmationModal = ({
  children,
  paths,
}: LeaveConfirmationModalProps) => {
  const [step, setStep] = useState<Step | null>(null);
  const section = useRef('');
  const { paths: routerPaths, push, query } = useRouter();
  const modalTargetElement = useRef<HTMLElement | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const checkoutId = (query[PAYMENT_QUERY_PARAMS.checkoutId] ?? '') as string;
  const reviewPath = routerPaths.store.checkout.review.url({
    query: {
      [PAYMENT_QUERY_PARAMS.checkoutId]: checkoutId,
    },
  });

  const targetPaths: Record<Step, UrlObject> = {
    delivery: paths?.delivery || routerPaths.store.checkout.delivery.url(),
    review: paths?.review || reviewPath,
    payment: paths?.payment || routerPaths.store.checkout.payment.url(),
  };

  const handleStepLeave: LeaveHandler =
    (step, sectionValue = '') =>
    () => {
      setStep(step);
      section.current = sectionValue;
    };

  useEffect(() => {
    modalTargetElement.current = document.getElementById('modal-root');
  }, []);

  useEffect(() => {
    if (step) {
      cancelButtonRef.current?.focus();
    }
  }, [step]);

  if (!modalTargetElement.current || !step) {
    return children(handleStepLeave);
  }

  const portal = createPortal(
    <>
      <S.StyledOverlay
        data-test-id='CheckoutLeaveConfirmationModalOverlay'
        onClick={() => setStep(null)}
      >
        <S.PortalContentWrapper $showModal aria-modal>
          <>
            <S.ModalHeader data-test-id='CheckoutLeaveConfirmationModalHeader'>
              <FormattedMessage {...commonMessages.areYouSure} />
            </S.ModalHeader>
            <S.ModalBody data-test-id='CheckoutLeaveConfirmationModalBody'>
              <S.ModalDescription>
                {M.ReturnToTheStep(STEP_INDEX.indexOf(step) + 1)}
              </S.ModalDescription>
            </S.ModalBody>
            <S.ModalFooter data-test-id='CheckoutLeaveConfirmationModalFooter'>
              <S.ModalFooterWrapper>
                <Button
                  ref={cancelButtonRef}
                  data-test-id='CheckoutLeaveConfirmationModalCancelButton'
                  onClick={() => setStep(null)}
                  variant='outlined'
                >
                  <FormattedMessage {...commonMessages.cancel} />
                </Button>
                <Button
                  data-test-id='CheckoutLeaveConfirmationModalOkButton'
                  onClick={() =>
                    push({
                      pathname: targetPaths[step]?.pathname,
                      query: {
                        ...(targetPaths[step]?.query as Record<string, string>),
                        section: section.current,
                      },
                    })
                  }
                >
                  <FormattedMessage {...commonMessages.ok} />
                </Button>
              </S.ModalFooterWrapper>
            </S.ModalFooter>
          </>
        </S.PortalContentWrapper>
      </S.StyledOverlay>
      ,
    </>,
    modalTargetElement.current,
  );

  return (
    <>
      {children(handleStepLeave)}
      {portal}
    </>
  );
};

Checkout.Header = Header;
Checkout.Content = Content;
Checkout.Section = Section;
Checkout.Stepper = CheckoutStepper;
Checkout.ServerErrors = ServerErrors;
Checkout.LeaveConfirmationModal = LeaveConfirmationModal;

export { Checkout };
